import React from 'react';
import { toast as reactToastify, TypeOptions } from 'react-toastify';

import { Toast } from '../toast';
import { ToastApi, ToastId, ToastOptions, ToastRenderProps } from './types';

/** The options that decide what a Toast renders, as opposed to how it behaves. */
type ToastBody = Pick<ToastOptions, 'closeButton' | 'content' | 'title' | 'variant'>;

type ToastState = {
  body: ToastBody;
  props?: ToastRenderProps;
};

/**
 * What each live Toast was last rendered with, so `update` can change one field
 * and leave the rest standing. Entries go when react-toastify drops the Toast.
 */
const state = new Map<ToastId, ToastState>();
let subscribed = false;

const remember = (id: ToastId, next: ToastState) => {
  if (!subscribed) {
    subscribed = true;
    reactToastify.onChange((item) => {
      if (item.status === 'removed') state.delete(item.id);
    });
  }
  state.set(id, next);
};

const toastifyType = (variant: ToastOptions['variant']): TypeOptions =>
  variant && variant !== 'loading' ? variant : 'default';

/** react-toastify's own options: everything the Design System does not own. */
const forwardable = (options: ToastOptions) => {
  const {
    closeButton: _closeButton,
    content: _content,
    placement: _placement,
    title: _title,
    variant: _variant,
    ...rest
  } = options;

  return rest;
};

/** The body keys `options` actually supplied, so an update can merge the rest. */
const suppliedBody = (options: ToastOptions): ToastBody => {
  const body: ToastBody = {};
  if ('closeButton' in options) body.closeButton = options.closeButton;
  if ('content' in options) body.content = options.content;
  if ('title' in options) body.title = options.title;
  if ('variant' in options) body.variant = options.variant;

  return body;
};

const renderToast =
  ({ closeButton, content, title, variant }: ToastBody, props?: ToastRenderProps) =>
  ({ closeToast }: { closeToast?: () => void }) =>
    (
      <Toast {...props} closeButton={closeButton} closeToast={closeToast} title={title} variant={variant}>
        {content}
      </Toast>
    );

const notify: ToastApi['notify'] = (options, props) => {
  const { closeButton, content, placement, title, variant = 'info' } = options;
  const body: ToastBody = { closeButton, content, title, variant };

  const id = reactToastify(renderToast(body, props), {
    type: toastifyType(variant),
    ...(placement && { position: placement }),
    ...forwardable(options),
  });
  remember(id, { body, props });

  return id;
};

const update: ToastApi['update'] = (id, options = {}, props) => {
  const previous = state.get(id);
  const supplied = suppliedBody(options);
  const rerenders = Object.keys(supplied).length > 0 || props !== undefined;
  const placement = options.placement && { position: options.placement };

  // An untracked Toast with nothing new to render is a plain option change —
  // leave whatever it is showing alone.
  if (!previous && !rerenders) {
    reactToastify.update(id, { ...placement, ...forwardable(options) });

    return;
  }

  const body: ToastBody = { variant: 'info', ...previous?.body, ...supplied };
  const mergedProps = props || previous?.props ? { ...previous?.props, ...props } : undefined;

  reactToastify.update(id, {
    render: renderToast(body, mergedProps),
    type: toastifyType(body.variant),
    ...placement,
    ...forwardable(options),
  });
  remember(id, { body, props: mergedProps });
};

const promise: ToastApi['promise'] = (task, { pending, success, error }, options = {}, props) => {
  const placement = options.placement && { position: options.placement };
  const stage = (body: ToastBody, fallback: ToastOptions['variant']) => {
    const variant = body.variant ?? fallback;

    return {
      render: renderToast({ ...body, closeButton: options.closeButton, variant }, props),
      type: toastifyType(variant),
      ...placement,
      ...forwardable(options),
    };
  };

  return reactToastify.promise(task, {
    pending: stage(pending, 'loading'),
    success: stage(success, 'success'),
    error: stage(error, 'error'),
  });
};

/**
 * Imperative Toast API. Identical to what `useToast` returns, but callable from
 * outside a component — a service module, a fetch interceptor, a store — where
 * a hook cannot go. Needs a `ToastContainer` mounted once at the app root.
 *
 * @example
 * import { toast } from '@redsift/popovers';
 *
 * toast.success({ content: 'Changes saved' });
 * toast.error({ title: 'Error', content: 'Failed to save' });
 *
 * @example
 * // Raise now, resolve later
 * const id = toast.loading({ content: 'Uploading…', autoClose: false });
 * toast.update(id, { content: 'Uploaded', variant: 'success', autoClose: 5000 });
 */
export const toast: ToastApi = {
  notify,
  clearWaitingQueue: reactToastify.clearWaitingQueue,
  dismiss: reactToastify.dismiss,
  done: reactToastify.done,
  error: (options, props) => notify({ ...options, variant: 'error' }, props),
  isActive: reactToastify.isActive,
  info: (options, props) => notify({ ...options, variant: 'info' }, props),
  loading: (options, props) => notify({ ...options, variant: 'loading' }, props),
  promise,
  success: (options, props) => notify({ ...options, variant: 'success' }, props),
  update,
  warning: (options, props) => notify({ ...options, variant: 'warning' }, props),
};
